/**
 * Tour — one-time first-visit guided walkthrough.
 *
 * Fires automatically the first time a browser loads ScoutDex.  After
 * completion or skip, persists `scoutdex_tour_v1_seen` to localStorage so
 * the user never sees it again.  Version-suffix lets us re-trigger after
 * a major UX change by bumping the key.
 *
 * Two highlight steps + a finale:
 *   1. Mic button       (the killer feature; non-obvious)
 *   2. Prompts (?)      (helps low-context users get unstuck)
 *   3. Sample question  ("Try one →" runs a starter query)
 *
 * Visual: dimmed backdrop with a spotlight cutout (single div + huge
 * box-shadow trick), a glass callout card pointing at the target with a
 * triangular tail, and a primary CTA in scout-blue.  No external library
 * — the aesthetic matches the rest of the app exactly.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

const STORAGE_KEY = "scoutdex_tour_v1_seen";

interface TourStep {
  id: string;
  targetSelector: string | null;
  title: string;
  body: string;
  cta?: string;        // overrides "Next" on this step
  samplePrompt?: string;
}

const STEPS: TourStep[] = [
  {
    id: "mic",
    targetSelector: '[data-tour-id="mic"]',
    title: "Speak your question",
    body:
      "Tap the mic, ask anything about Pokémon GO, then tap again to send. " +
      "On desktop you can hold Space to talk.",
  },
  {
    id: "prompts",
    targetSelector: '[data-tour-id="prompts"]',
    title: "Not sure what to ask?",
    body:
      "Browse example prompts — IV rankings, raid counters, optimal " +
      "movesets, lore, and more.",
  },
  {
    id: "ready",
    targetSelector: null,   // centered, no spotlight target
    title: "Ready to scout?",
    body:
      "We'll fire a sample question to show you what Scout can do — " +
      "voice narration and a visual card, side by side.",
    cta: "Try a sample question →",
    samplePrompt: "What are Mewtwo's base stats?",
  },
];

interface TourProps {
  onSamplePrompt: (question: string) => void;
}

export default function Tour({ onSamplePrompt }: TourProps) {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  // First-visit trigger — small delay so the page can settle.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return; // localStorage blocked (private mode etc.) — skip the tour
    }
    const t = setTimeout(() => setActive(true), 800);
    return () => clearTimeout(t);
  }, []);

  // Measure the current step's target on every step change + window resize.
  useLayoutEffect(() => {
    if (!active) return;
    const step = STEPS[stepIdx];
    const measure = () => {
      if (!step.targetSelector) {
        setTargetRect(null);
        return;
      }
      const el = document.querySelector(step.targetSelector);
      if (!el) {
        setTargetRect(null);
        return;
      }
      setTargetRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, stepIdx]);

  // Allow Esc to skip
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleSkip();
      else if (e.key === "Enter") handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIdx]);

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore — at worst they see the tour again next visit */
    }
  }, []);

  const handleSkip = useCallback(() => {
    setActive(false);
    markSeen();
  }, [markSeen]);

  const handleNext = useCallback(() => {
    const step = STEPS[stepIdx];
    const isLast = stepIdx === STEPS.length - 1;
    if (isLast) {
      setActive(false);
      markSeen();
      if (step.samplePrompt) onSamplePrompt(step.samplePrompt);
    } else {
      setStepIdx((i) => i + 1);
    }
  }, [stepIdx, markSeen, onSamplePrompt]);

  if (!active) return null;

  const step = STEPS[stepIdx];
  const nextLabel = step.cta ?? "Next";

  // Compute callout position.
  //   - If we have a target: place the callout above the target (since both
  //     tour targets live in the InputBar at the bottom), horizontally
  //     aligned with the target's center.
  //   - If no target (finale): center the callout in the viewport.
  const calloutWidth = Math.min(360, typeof window !== "undefined" ? window.innerWidth - 32 : 360);

  let calloutStyle: React.CSSProperties;
  // Both current tour targets live in the InputBar at the bottom of the
  // viewport, so the callout always sits above them with a downward tail.
  // If we ever add a step pointing at the header, swap this to "up".
  let showArrow = false;
  if (targetRect) {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const left = Math.max(
      16,
      Math.min(
        (typeof window !== "undefined" ? window.innerWidth : 1024) - calloutWidth - 16,
        targetCenterX - calloutWidth / 2,
      ),
    );
    // Place callout above target, with 20px gap
    const top = Math.max(16, targetRect.top - 20);
    calloutStyle = {
      position: "fixed",
      top,
      left,
      width: calloutWidth,
      transform: "translateY(-100%)",
    };
    showArrow = true;
  } else {
    calloutStyle = {
      position: "fixed",
      top: "50%",
      left: "50%",
      width: calloutWidth,
      transform: "translate(-50%, -50%)",
    };
  }

  // Spotlight cutout: a single positioned div sized to the target, with a
  // huge box-shadow that effectively dims everything outside it.  Cheap,
  // GPU-friendly, no SVG masking, no library.
  const SPOTLIGHT_PAD = 6;
  const spotlightStyle: React.CSSProperties | null = targetRect
    ? {
        position: "fixed",
        top: targetRect.top - SPOTLIGHT_PAD,
        left: targetRect.left - SPOTLIGHT_PAD,
        width: targetRect.width + SPOTLIGHT_PAD * 2,
        height: targetRect.height + SPOTLIGHT_PAD * 2,
        borderRadius: "12px",
        boxShadow:
          "0 0 0 9999px rgba(0, 0, 0, 0.78), 0 0 0 2px rgba(59, 130, 246, 0.5), 0 0 24px rgba(59, 130, 246, 0.35)",
        pointerEvents: "none",
        transition: "all 220ms cubic-bezier(0.4, 0, 0.2, 1)",
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={`Tour step ${stepIdx + 1} of ${STEPS.length}: ${step.title}`}
    >
      {/* Click-blocker that absorbs taps on the dimmed area but not on the
          spotlight target — though target events are also pointer-blocked
          via the spotlight div's pointer-events:none combined with this
          backdrop being separate. */}
      {targetRect ? (
        <>
          {/* Full-screen pointer trap */}
          <div className="absolute inset-0" />
          {/* Spotlight ring + dim-everything-else */}
          <div aria-hidden="true" style={spotlightStyle!} />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/78 backdrop-blur-[2px]" />
      )}

      {/* Callout card */}
      <div style={calloutStyle} className="animate-slide-up">
        <div className="relative bg-scout-bg/95 backdrop-blur-md border border-white/[0.10] rounded-2xl shadow-2xl p-5">
          {/* Skip in top-right */}
          <button
            type="button"
            onClick={handleSkip}
            className="absolute top-3 right-3 text-[11px] uppercase tracking-wider text-white/35 hover:text-white/70 transition-colors px-2 py-1"
            aria-label="Skip tour"
          >
            Skip
          </button>

          {/* Step progress dots */}
          <div className="flex items-center gap-1.5 mb-3">
            {STEPS.map((s, i) => (
              <span
                key={s.id}
                className={`h-1 rounded-full transition-all ${
                  i === stepIdx
                    ? "w-6 bg-scout-blue"
                    : i < stepIdx
                      ? "w-1.5 bg-white/30"
                      : "w-1.5 bg-white/10"
                }`}
                aria-hidden="true"
              />
            ))}
          </div>

          {/* Body */}
          <h3 className="text-base font-semibold text-white mb-1.5 pr-12">
            {step.title}
          </h3>
          <p className="text-sm text-white/65 leading-relaxed mb-4">
            {step.body}
          </p>

          {/* Action row */}
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-white/30 select-none">
              {stepIdx + 1} of {STEPS.length}
            </span>
            <button
              type="button"
              onClick={handleNext}
              className="px-4 py-2 rounded-xl bg-scout-blue text-white text-sm font-medium hover:bg-blue-600 transition-colors shadow-lg shadow-scout-blue/20"
            >
              {nextLabel}
            </button>
          </div>

          {/* Triangular tail pointing down toward the target.  Rendered as
              a rotated div so it inherits the card's border + background
              colors cleanly. */}
          {showArrow && (
            <div
              aria-hidden="true"
              className="absolute left-1/2 -bottom-2 w-4 h-4 -translate-x-1/2 rotate-45 bg-scout-bg/95 border-r border-b border-white/[0.10]"
            />
          )}
        </div>
      </div>
    </div>
  );
}
